import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { HomeOutlined, ReloadOutlined } from '@ant-design/icons';
import { Button, Result } from 'antd';
import { ROUTES } from '@/constants';
import { isChunkLoadError, reloadOnceForChunkError } from '@/utils/chunkReload';

/**
 * Shared error screen for the router errorElement and the top-level
 * ErrorBoundary. Chunk-load errors (stale build after a deploy) trigger one
 * automatic reload; everything else gets a readable message with actions.
 *
 * Navigation uses full page loads on purpose: after an error the in-memory
 * app may be stale, a fresh index.html is the safe way back.
 */
export default function AppErrorView({ error }) {
  // Decide once per mount; re-renders must not trigger extra reloads.
  const [reloading] = useState(
    () => isChunkLoadError(error) && reloadOnceForChunkError()
  );

  // Translations may be unavailable (they are fetched over HTTP), so never suspend.
  const { t } = useTranslation('translations', { useSuspense: false });

  if (reloading) return null;

  const message =
    error?.message ??
    error?.statusText ??
    (typeof error === 'string' ? error : '');

  return (
    <div className="flex min-h-screen items-center justify-center bg-white p-4 dark:bg-zinc-900">
      <Result
        status="warning"
        title={t('appError.title', { defaultValue: 'Something went wrong' })}
        subTitle={t('appError.text', {
          defaultValue:
            'The page could not be displayed. Refresh the page; if the problem persists, contact support.',
        })}
        extra={[
          <Button
            key="reload"
            type="primary"
            shape="round"
            icon={<ReloadOutlined />}
            onClick={() => window.location.reload()}
          >
            {t('appError.reload', { defaultValue: 'Refresh' })}
          </Button>,
          <Button
            key="home"
            shape="round"
            icon={<HomeOutlined />}
            onClick={() => window.location.assign(ROUTES.admin)}
          >
            {t('appError.home', { defaultValue: 'Back to home' })}
          </Button>,
        ]}
      >
        {message ? (
          <p className="m-0 break-words text-center text-xs text-zinc-400">
            {message}
          </p>
        ) : null}
      </Result>
    </div>
  );
}
